/*
 * File: refresh-token.interceptor.ts
 * Project: rast-app
 * Create Date: Tuesday, 5th August 2025
 * Author: Pedro Santos (pedro.santos@vonbraunlabs.com)
 * -----
 * Last Modified: Monday, 10th November 2025 5:07:38 pm
 * Modified by: Pedro Santos (pedro.santos@vonbraunlabs.com)
 * -----
 * Copyright (c) 2025 CPA Wernher von Braun
 * ----
 * HISTORY:
 * Date      	By	Comments
 * ----------	---	---------------------------------------------------------
 */

import { HttpErrorResponse, HttpEvent, HttpEventType, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { LoginResponseDto } from '@customTypes';
import { environment } from '@env';
import { catchError, find, Observable, switchMap, throwError } from 'rxjs';
import { BiometricAuthService } from '../../services/biometric-auth/biometric-auth.service';

export function refreshTokenInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const router = inject(Router);
  const biometricAuthService = inject(BiometricAuthService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const unauthorizedStatus = 401;
      const refreshToken = localStorage.getItem('refreshToken');
      if (error.status === unauthorizedStatus && refreshToken !== null)
        return handleUnauthorizedError(req, next, refreshToken, router, biometricAuthService);

      return throwError(() => error);
    })
  );
}

function handleUnauthorizedError(
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  refreshToken: string,
  router: Router,
  biometricAuthService: BiometricAuthService
): Observable<HttpEvent<any>> {
  const refreshReq = new HttpRequest('POST', `${environment.apiUrl}/refresh`, { refreshToken: refreshToken });

  return next(refreshReq).pipe(
    find(res => res.type === HttpEventType.Response),
    switchMap(res => {
      if (res && res.type === HttpEventType.Response) {
        const tokens: LoginResponseDto = res.body as LoginResponseDto;

        localStorage.setItem('refreshToken', tokens.refreshToken);
        localStorage.setItem('accessToken', tokens.accessToken);
        biometricAuthService.resyncRotatedRefreshToken(tokens.refreshToken);

        return next(req.clone({ setHeaders: { Authorization: `Bearer ${tokens.accessToken}` } }));
      }

      return throwError(() => new Error('Refresh token response not emitted'));
    }),
    catchError(err => {
      if (err.status !== 404) {
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('accessToken');

        router.navigateByUrl('/auth/login');
      }
      return throwError(() => err);
    })
  );
}
